/* Uit Krabbelpaal.dc.html; bodies letterlijk overgenomen. */

import { IDLE_MS, api, asJson } from '../lib/api.js';
import { LEGACY_AUTHOR_IDS, PB_URL } from '../lib/config.js';
import { hasPixels, setDefaultAvatar } from '../lib/pixels.js';
import { dayKey } from '../lib/text.js';
import * as store from '../lib/store.js';

export default {
  // Eenmalig: oude records zonder accountId alsnog aan hun account koppelen,
  // zodat naamswijzigingen daarna nooit meer handwerk zijn.
  async backfillAccountIds() {
      if (this.backfillRan || store.get('gp_backfill_ids_v1')) return;
      const accounts = this.state.accounts || [];
      if (!accounts.length) return;
      this.backfillRan = true;
      const byName = {};
      accounts.forEach((a) => { byName[a.name] = (byName[a.name] ? 'dupe' : a.id); });
      const jobs = [
        { coll: this.collectionName, field: 'accountId' },
        { coll: 'paal_messages', field: 'accountId' },
        { coll: 'paal_feature_requests', field: 'authorId' }
      ];
      let patched = 0;
      for (const job of jobs) {
        try {
          const filter = encodeURIComponent(job.field + '=""');
          const res = await api(`${PB_URL}/api/collections/${job.coll}/records?filter=${filter}&perPage=200&fields=id,author`);
          if (!res.ok) continue;
          const data = await res.json();
          for (const r of (data.items || [])) {
            const id = byName[(r.author || '').trim()] || LEGACY_AUTHOR_IDS[(r.author || '').trim()];
            if (!id || id === 'dupe') continue;
            const body = {}; body[job.field] = id;
            const p = await api(`${PB_URL}/api/collections/${job.coll}/records/${r.id}`, asJson('PATCH', body));
            if (p.ok) patched++;
          }
        } catch (e) {}
      }
      store.set('gp_backfill_ids_v1', String(Date.now()));
      if (patched) { this.fetchChat(); this.refreshGallery(true); }
    },

  async beat() {
      if (!this.state.accountId) return;
      const sec = this.tickActiveTime();
      this.pushedSec = sec;
      try {
        await api(PB_URL + '/api/collections/paal_accounts/records/' + this.state.accountId, asJson('PATCH', { lastSeen: new Date().toISOString(), activeSec: sec, activeDay: dayKey() }));
      } catch (e) {}
    },

  // Teller van je tijd op de site vandaag. Loopt elke 5 seconden door zolang het
  // tabblad zichtbaar is; een gat groter dan 20 s telt als pauze. Wie even langskomt
  // krijgt minimaal een minuut, zodat er nooit een lege 0 in het logboek staat.
  tickActiveTime(start) {
      const today = dayKey();
      let day = '', sec = 0;
      try {
        day = store.get('gp_time_day') || '';
        sec = Number(store.get('gp_time_sec') || 0) || 0;
      } catch (e) {}
      if (day !== today) { sec = 0; this.lastTickAt = 0; }
      const now = Date.now();
      if (start || !this.lastTickAt) {
        sec = Math.max(sec, 60);
      } else {
        const gap = now - this.lastTickAt;
        if (gap > 0 && gap <= 20000) sec += gap / 1000;
      }
      this.lastTickAt = now;
      sec = Math.round(sec);
      try {
        store.set('gp_time_day', today);
        store.set('gp_time_sec', String(sec));
      } catch (e) {}
      return sec;
    },

  async fetchAccounts() {
      if (!this.state.connected && !PB_URL) return;
      try {
        const res = await api(`${PB_URL}/api/collections/paal_accounts/records?sort=created&perPage=200`);
        if (res.status === 404) { this.setState({ accountsMissing: true }); return; }
        if (!res.ok) { this.notify('Accounts konden niet geladen worden (' + res.status + ').'); return; }
        const data = await res.json();
        this.noteAccSince(data.items);
        const accounts = (data.items || []).map((r) => ({ id: r.id, name: r.name || 'onbekend', avatar: r.avatar || '', bio: r.bio || '', favFood: r.favFood || '', lastSeen: r.lastSeen || r.updated || '', pinHash: r.pinHash || '', created: r.created || '', activeSec: typeof r.activeSec === 'number' ? r.activeSec : null, activeDay: r.activeDay || '', beta: !!r.beta }));
        const tester = accounts.find((a) => (a.name || '').toLowerCase() === 'testaccount');
        if (tester && hasPixels(tester.avatar)) setDefaultAvatar(tester.avatar);
        this.announceOnline(accounts);
        this.setState({ accountsMissing: false, accounts }, () => { this.syncMyAccount(accounts); this.backfillAccountIds(); });
      } catch (e) {}
    },

  // Het nieuwste tijdstempel dat we van de server zagen. Daarmee vragen we de
  // volgende keer alleen naar wat sindsdien veranderde.
  noteAccSince(items) {
      (items || []).forEach((r) => {
        const u = r.updated || '';
        if (u && (!this.accSince || u > this.accSince)) this.accSince = u;
      });
    },

  // Wie is er online: alleen de velden die daarvoor nodig zijn, en alleen de
  // regels die sinds de vorige keer veranderden. Dat is een paar honderd tekens
  // in plaats van 200 volledige accounts (~94 KB) elke vijftien seconden. De
  // volledige lijst komt elke twee minuten langs; die vult bio, pincode en de
  // rest weer bij en merkt verwijderde accounts op.
  async fetchPresence() {
      if (!PB_URL) return;
      const have = this.state.accounts || [];
      if (!have.length || !this.accSince) { this.fetchAccounts(); return; }
      try {
        const q = encodeURIComponent('updated>"' + this.accSince + '"');
        const res = await api(`${PB_URL}/api/collections/paal_accounts/records?perPage=200&filter=${q}&fields=id,name,avatar,lastSeen,activeSec,activeDay,beta,updated`);
        if (!res.ok) return;
        const items = ((await res.json()).items) || [];
        if (!items.length) return;
        // Een account dat we nog niet kennen: dan alles ophalen, zodat er geen
        // halve regel zonder pincode of bio in de lijst belandt.
        if (items.some((r) => !have.some((a) => a.id === r.id))) { this.fetchAccounts(); return; }
        this.noteAccSince(items);
        const byId = {};
        items.forEach((r) => { byId[r.id] = r; });
        const accounts = have.map((a) => {
          const r = byId[a.id];
          if (!r) return a;
          return Object.assign({}, a, {
            name: r.name || a.name,
            avatar: r.avatar || '',
            lastSeen: r.lastSeen || r.updated || a.lastSeen,
            activeSec: typeof r.activeSec === 'number' ? r.activeSec : a.activeSec,
            activeDay: r.activeDay || '',
            // Ook in de vraag van vijftien seconden, zodat een pas aangevinkte
            // tester niet twee minuten hoeft te wachten tot de volledige lijst
            // weer langskomt.
            beta: !!r.beta
          });
        });
        this.announceOnline(accounts);
        this.setState({ accounts }, () => this.syncMyAccount(accounts));
      } catch (e) {}
    },

  noteAct() {
      const wasIdle = Date.now() - (this.lastAct || 0) > IDLE_MS;
      this.lastAct = Date.now();
      if (wasIdle && !document.hidden) this.catchUp();
    },

  // Twee minuten geen klik of toets: van vijftien naar vijfenveertig seconden.
  // De hartslag zelf blijft op tempo, anders lijk je voor anderen offline.
  pollSkip() {
      if (Date.now() - (this.lastAct || 0) < IDLE_MS) return false;
      return Math.floor(Date.now() / 15000) % 3 !== 0;
    },

  // Eerste aanraking na een stille periode: meteen bijwerken, zodat je nooit
  // naar oud nieuws zit te kijken.
  catchUp() {
      this.beat();
      this.fetchPresence();
      this.fetchDms();
      this.fetchDuo();
      this.fetchChat();
      this.fetchDayData();
    }
};
